import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_Updater,
} from "material-react-table";
import Button from "@mui/material/Button";
import { useMemo, useState } from "react";
import { MRTNoMemo } from "_shared/_views/tables/table";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import SearchMatch from "_shared/_views/tables/search-match";
import { DateCell } from "_shared/_views/tables/date-cells";
import { StatusChip } from "_shared/_views/tables/status-chip";
import { formatDate } from "_shared/formatting/dates";
import { invitationStatusTone } from "legal-entities/invitations/statuses/invitation-status-tone";
import { LIST_PAGE_SIZE } from "_shared/_views/tables/table-page-size";
import {
  CollectionToolbar,
  CollectionToolbarSearch,
  CollectionToolbarViewButtons,
  searchTextFieldProps,
} from "_shared/_views/tables/table-toolbar";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import {
  invitationOrderFrom,
  invitationStatusFrom,
  NAME_COLUMN,
  SENT_COLUMN,
  STATUS_COLUMN,
} from "legal-entities/invitations/member-invitations/_views/entity-member-invitations-table";
import type { ActiveInvitationStatus } from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";
import UserEntityMemberInvitation from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import {
  useGetActiveUserInvitationOrganisationsQuery,
  useGetActiveUserInvitationsQuery,
} from "users/super-users/_api/super-user-api";

/** The Organisation column's id, and the column whose filter the server applies. */
export const ORGANISATION_COLUMN = "organisation";

const columnHelper = createMRTColumnHelper<UserEntityMemberInvitation>();

/** The statuses an active invitation can have, and so the only ones worth offering as a filter. */
const ACTIVE_STATUSES: ActiveInvitationStatus[] = [InvitationStatus.Open, InvitationStatus.Expired];

/**
 * Put away until the reader shows it: the organization reads under the invitee's name instead (#6722).
 * The column stays in Show/hide, with its filter, and the Filter sheet on a phone reaches it there too.
 */
export const HIDDEN_UNTIL_CHOSEN: Readonly<Record<string, boolean>> = { [ORGANISATION_COLUMN]: false };

/** The invitee's whole name, as the Name column reads and searches it. */
function inviteeName(invitation: UserEntityMemberInvitation): string {
  return `${invitation.registrationInfo.name.firstName} ${invitation.registrationInfo.name.lastName}`;
}

function InviteeNameCell(
  props: Readonly<{
    invitation: UserEntityMemberInvitation;
    table: MRT_TableInstance<UserEntityMemberInvitation>;
    isOrganizationSearched: boolean;
  }>,
) {
  // SearchMatch reads the term from MRT's one mutable table instance; a memoized cell keeps the old one.
  "use no memo";

  const { invitation, table, isOrganizationSearched } = props;
  const organization = invitation.userEntityInfo.entityName.value;
  // Shown, the Company column has the organization to itself; hidden, as it is until chosen, it reads here.
  const isOrganizationUnderName = table.getState().columnVisibility?.[ORGANISATION_COLUMN] === false;
  return (
    <CondensedCell
      data-testid="invitee-name-cell"
      title={<SearchMatch text={inviteeName(invitation)} table={table} />}
      secondary={
        isOrganizationUnderName && (isOrganizationSearched ? <SearchMatch text={organization} table={table} /> : organization)
      }
    />
  );
}

/** Newest first, the server's own default. Never empty: the server orders every answer. */
const DEFAULT_SORTING: SortingState = [{ id: SENT_COLUMN, desc: true }];


/**
 * The id of the organisation picked in the Organisation column's filter, if any.
 *
 * The id's value, not a `Guid`: it goes into the query's argument, where a `Guid` built during render
 * would be a new argument on every render and a failed read would be sent again each time (#6488).
 */
export function invitationOrganisationFrom(columnFilters: ColumnFiltersState): string | undefined {
  const value = columnFilters.find((filter) => filter.id === ORGANISATION_COLUMN)?.value;
  return typeof value === "string" && value ? value : undefined;
}

/** MRT hands state changes as either a value or a function of the old one. */
function applyUpdater<TState>(updater: MRT_Updater<TState>, current: TState): TState {
  return typeof updater === "function" ? (updater as (old: TState) => TState)(current) : updater;
}

/**
 * Every organisation's active member invitations, searched, filtered, ordered and paged on the server
 * (#5447). The list is platform-wide, so it grows with every organisation's invitations.
 */
export default function UserEntityMemberInvitationsTable() {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: LIST_PAGE_SIZE });

  const order = invitationOrderFrom(sorting);
  const {
    currentData: page,
    data: lastAnswer,
    error,
    isFetching,
    isLoading,
    refetch,
  } = useGetActiveUserInvitationsQuery({
    legalEntityId: invitationOrganisationFrom(columnFilters),
    search: globalFilter,
    status: invitationStatusFrom(columnFilters),
    orderBy: order.orderBy,
    orderDir: order.orderDir,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });
  // A separate request: the page's own Retry and a new search or page never ask for it again, so its
  // failure carries its own Retry rather than leaving the Organisation filter empty without a word.
  const {
    data: organisations,
    error: organisationsError,
    isFetching: isFetchingOrganisations,
    refetch: refetchOrganisations,
  } = useGetActiveUserInvitationOrganisationsQuery();
  // currentData, not data: data keeps the last answer while another loads and after it fails, which
  // would show the rows of a search already replaced, under its error.
  const invitations = page?.data;

  const organisationOptions = useMemo(
    () =>
      (organisations ?? []).map((organisation) => ({
        label: organisation.legalEntityName,
        value: organisation.legalEntityId.value,
      })),
    [organisations],
  );

  // The server searches an organization's name only across organizations: within the one the filter
  // picked, every row would match. Marking it then would claim a match the search never made (#6590).
  const isOrganizationSearched = !invitationOrganisationFrom(columnFilters);

  // MRT marks the search term in the cells it draws, in the columns the search reads: the invitee's
  // name and email, and the organization. The status and the dates are never searched (#6590).
  const columns = useMemo(
    () => [
      columnHelper.accessor(inviteeName, {
        header: "Name",
        id: NAME_COLUMN,
        enableColumnFilter: false,
        meta: { kind: "name" },
        Cell: ({ row, table }) => (
          <InviteeNameCell invitation={row.original} table={table} isOrganizationSearched={isOrganizationSearched} />
        ),
      }),
      // Searched with the name, but not sorted on its own: the server orders by name, then email.
      columnHelper.accessor((invitation) => invitation.registrationInfo.email.value, {
        header: "Email",
        id: "email",
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          kind: "text",
          fold: { into: NAME_COLUMN, priority: 1, render: (invitation) => invitation.registrationInfo.email.value },
        },
      }),
      columnHelper.accessor((invitation) => invitation.userEntityInfo.entityName.value, {
        header: "Company",
        id: ORGANISATION_COLUMN,
        enableGlobalFilter: isOrganizationSearched,
        enableSorting: false,
        filterVariant: "select",
        filterSelectOptions: organisationOptions,
        meta: { kind: "text" },
      }),
      columnHelper.accessor((invitation) => invitation.status, {
        header: "Status",
        id: STATUS_COLUMN,
        enableGlobalFilter: false,
        enableSorting: false,
        filterVariant: "select",
        filterSelectOptions: ACTIVE_STATUSES,
        meta: { kind: "status" },
        Cell: ({ row }) => (
          <StatusChip label={row.original.status} tone={invitationStatusTone(row.original.status)} />
        ),
      }),
      columnHelper.accessor((invitation) => formatDate(invitation.sentDate) ?? "", {
        header: "Sent",
        id: SENT_COLUMN,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        meta: { kind: "date" },
        Cell: ({ row }) => <DateCell value={row.original.sentDate} />,
      }),
      columnHelper.accessor((invitation) => formatDate(invitation.expires) ?? "", {
        header: "Expires",
        id: "expires",
        enableColumnFilter: false,
        enableGlobalFilter: false,
        enableSorting: false,
        meta: { kind: "date" },
        Cell: ({ row }) => <DateCell value={row.original.expires} />,
      }),
    ],
    [isOrganizationSearched, organisationOptions],
  );

  const tableOptions: MRT_TableOptions<UserEntityMemberInvitation> = {
    columns,
    data: invitations ?? [],
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableRowSelection: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    initialState: { columnVisibility: { ...HIDDEN_UNTIL_CHOSEN }, showGlobalFilter: true },
    // The search and the view buttons on one wrapping row, as the users table beside it draws them.
    positionGlobalFilter: "none",
    renderTopToolbarCustomActions: ({ table }) => (
      <CollectionToolbar>
        <CollectionToolbarSearch>
          <GlobalFilterTextField table={table} />
        </CollectionToolbarSearch>
        <CollectionToolbarViewButtons>
          <TableToolsMenu table={table} />
        </CollectionToolbarViewButtons>
      </CollectionToolbar>
    ),
    // An empty fragment rather than `null`: MRT reads this slot with `??`, and a nullish return would
    // draw its default buttons a second time.
    renderToolbarInternalActions: () => <></>,
    muiSearchTextFieldProps: ({ table }) => ({
      ...searchTextFieldProps({ searches: "invitations", table }),
      size: "small",
      variant: "outlined",
    }),
    getRowId: (invitation, index) => invitation.id?.value ?? `row-${index}`,
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: Boolean(error),
      isLoading: isFetching,
      onRetry: () => void refetch(),
      subject: "invitations",
    }),
    muiToolbarAlertBannerProps:
      error || organisationsError
        ? {
            color: "error",
            children: [
              error && "Error loading invitations.",
              organisationsError && "Error loading the organisations to filter by.",
            ]
              .filter(Boolean)
              .join(" "),
            action: organisationsError && (
              <Button color="inherit" disabled={isFetchingOrganisations} onClick={() => void refetchOrganisations()}>
                Retry organisations
              </Button>
            ),
          }
        : undefined,
    muiTableContainerProps: { sx: { flex: 1 } },
    muiTablePaperProps: { sx: { height: "100%", display: "flex", flexDirection: "column" } },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enableSortingRemoval: false,
    // The server orders by one column, so a second sort indicator would promise an order it never sends.
    enableMultiSort: false,
    // A new search, filter or order is a new list, and a new list starts on its first page.
    onColumnFiltersChange: (updater: MRT_Updater<ColumnFiltersState>) => {
      setColumnFilters(applyUpdater(updater, columnFilters));
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onGlobalFilterChange: (updater: MRT_Updater<string>) => {
      setGlobalFilter(applyUpdater(updater, globalFilter) ?? "");
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onPaginationChange: (updater: MRT_Updater<PaginationState>) => {
      const next = applyUpdater(updater, pagination);
      // A different page size is a different set of pages.
      setPagination(next.pageSize === pagination.pageSize ? next : { ...next, pageIndex: 0 });
    },
    onSortingChange: (updater: MRT_Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      setSorting(next.length > 0 ? next : DEFAULT_SORTING);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    // Every invitation that matches, from X-Pagination. While a page the cache has never held loads,
    // currentData is undefined, so the last answer's total holds the page rather than letting MRT
    // judge it out of bounds and send the reader back to page one (#5444).
    rowCount: (page ?? lastAnswer)?.totalElements ?? 0,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: Boolean(error || organisationsError),
      showProgressBars: isFetching,
      sorting,
    },
  };

  return (
    <MRTNoMemo<UserEntityMemberInvitation>
      options={tableOptions}
      persistColumnVisibilityAs="super-user-invitations"
      cards={{ defaultSorting: DEFAULT_SORTING }}
    />
  );
}
